import { Link } from "react-router-dom";

// Customer-site footer: logo + contact + browse + legal nav.
//
// Phase 4e CO-only storefront — neutral "Contact" framing without
// the internal "yard" vocabulary; one operating region (Colorado),
// one phone number.
//
// Phase 4e brand-visual-rollout (take 2) — bold brand-themed footer:
//
//   * Top hairline of brand-red continues the hero's red accent.
//   * Three-column grid on desktop (brand | contact | browse + account).
//   * flag-navy band along the bottom carrying the © + legal links —
//     reads as the "foundation stone" of the page in the same color
//     as the trust strip and hero.
//   * Phone number in brand-red so a contractor scanning the page
//     finds it instantly.
export function Footer(): JSX.Element {
  return (
    <footer className="relative border-t border-zinc-200 bg-brand-white text-zinc-700">
      {/* Hairline brand-red top accent — mirrors the hero. */}
      <div aria-hidden="true" className="absolute inset-x-0 top-0 h-0.5 bg-brand-red" />

      <div className="mx-auto max-w-6xl px-4 py-12 md:px-6 md:py-14">
        <div className="grid gap-10 md:grid-cols-3">
          <section>
            <Link to="/" aria-label="JDR Equipment Rentals — home" className="inline-block">
              <img
                src="/brand/jdr-logo-web.png"
                srcSet="/brand/jdr-logo-web.png 1x, /brand/jdr-logo-web@2x.png 2x"
                alt="JDR Equipment Rentals"
                width={128}
                className="h-auto w-32"
              />
            </Link>
            <p className="mt-4 max-w-xs text-sm text-zinc-600">
              Family-owned, Colorado-based equipment rental. Delivered, fueled,
              and inspected before it hits your jobsite.
            </p>
          </section>

          <section>
            <h2 className="text-xs font-semibold uppercase tracking-[0.18em] text-flag-navy">
              Contact
            </h2>
            <ul className="mt-4 space-y-3 text-sm">
              <li>
                <p className="font-semibold text-brand-black">Phone</p>
                <a
                  className="mt-1 inline-flex items-center gap-2 text-base font-semibold text-brand-red transition hover:text-brand-red-dark"
                  href="tel:+13034354328"
                >
                  <svg
                    aria-hidden="true"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="h-4 w-4"
                  >
                    <path d="M2 3.5A1.5 1.5 0 0 1 3.5 2h1.879a1.5 1.5 0 0 1 1.422 1.025l.83 2.49a1.5 1.5 0 0 1-.36 1.524L6.21 8.21a11.04 11.04 0 0 0 5.58 5.58l1.171-1.062a1.5 1.5 0 0 1 1.524-.36l2.49.83A1.5 1.5 0 0 1 18 14.62V16.5a1.5 1.5 0 0 1-1.5 1.5h-1A13.5 13.5 0 0 1 2 4.5v-1Z" />
                  </svg>
                  303-435-4328
                </a>
              </li>
              <li>
                <p className="font-semibold text-brand-black">Hours</p>
                <p className="text-zinc-600">Mon&ndash;Fri · 7am&ndash;5pm MT</p>
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xs font-semibold uppercase tracking-[0.18em] text-flag-navy">
              Browse &amp; account
            </h2>
            <ul className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <li>
                <Link to="/" className="text-zinc-700 transition hover:text-brand-red">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/search" className="text-zinc-700 transition hover:text-brand-red">
                  Search
                </Link>
              </li>
              <li>
                <Link to="/cart" className="text-zinc-700 transition hover:text-brand-red">
                  Cart
                </Link>
              </li>
              <li>
                <Link to="/sign-in" className="text-zinc-700 transition hover:text-brand-red">
                  Sign in
                </Link>
              </li>
            </ul>
          </section>
        </div>
      </div>

      {/* Phase 4e brand-visual-rollout (take 2) — flag-navy "foundation
          stone" band carrying the © + legal links. data-brand-hairline
          is retained so the existing Jest selector still finds the
          element (the band IS the hairline, just bolder). */}
      <div
        data-brand-hairline="flag-navy"
        className="bg-flag-navy text-brand-white"
      >
        <div className="mx-auto flex max-w-6xl flex-col gap-2 px-4 py-4 text-xs md:flex-row md:items-center md:justify-between md:px-6">
          <p>© {new Date().getFullYear()} JDR, LLC. All rights reserved.</p>
          <nav aria-label="Legal" className="flex flex-wrap gap-5">
            <Link to="/terms" className="text-brand-white/80 transition hover:text-brand-white">
              Terms
            </Link>
            <Link to="/privacy" className="text-brand-white/80 transition hover:text-brand-white">
              Privacy
            </Link>
          </nav>
        </div>
      </div>
    </footer>
  );
}
